/**
 * MobileDrawer Component
 * LAYER-SHELL: Unified mobile navigation drawer
 *
 * Slide-out navigation drawer for mobile devices.
 * Uses useNavigation hook for role-based nav item filtering.
 * Shows ALL navigation items the user's role can access.
 */
import React, { useEffect, useRef } from 'react';
import { XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { NavItem } from './NavItem';
import { useNavigation } from '../../hooks/useNavigation';
import type { UserRole } from './Shell';
import './MobileDrawer.scss';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userRole: UserRole;
  canViewAsCustomer?: boolean;
  viewAsCustomer?: boolean;
  onToggleViewAsCustomer?: () => void;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  userRole,
  canViewAsCustomer,
  viewAsCustomer,
  onToggleViewAsCustomer,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const navItems = useNavigation(userRole);

  // Focus trap, escape key handling, and body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const drawer = drawerRef.current;
    if (!drawer) return;

    // Get all focusable elements within the drawer
    const getFocusableElements = (): HTMLElement[] => {
      const elements = drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      return Array.from(elements);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle Escape key
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Handle Tab key for focus trap
      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: if on first element, wrap to last
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: if on last element, wrap to first
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    // Focus the close button when drawer opens
    closeButtonRef.current?.focus();

    document.addEventListener('keydown', handleKeyDown);

    // Prevent body scroll when drawer is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Close drawer when a nav item is clicked
  const handleNavClick = () => {
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`mobile-drawer-overlay ${isOpen ? 'mobile-drawer-overlay--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        className={`mobile-drawer ${isOpen ? 'mobile-drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        aria-hidden={!isOpen}
      >
        <div className="mobile-drawer__header">
          <img
            src="/assets/images/logos/esig-horizontal.svg"
            alt="eSignature Guarantee"
            className="mobile-drawer__logo mobile-drawer__logo--light"
          />
          <img
            src="/assets/images/logos/esig-horizontal-white.svg"
            alt="eSignature Guarantee"
            className="mobile-drawer__logo mobile-drawer__logo--dark"
          />
          <button
            ref={closeButtonRef}
            className="mobile-drawer__close-btn"
            onClick={onClose}
            aria-label="Close navigation menu"
          >
            <XMarkIcon className="mobile-drawer__close-icon" />
          </button>
        </div>

        <nav className="mobile-drawer__nav">
          <ul className="mobile-drawer__list">
            {navItems.map((item) => (
              <li key={item.id} onClick={handleNavClick}>
                <NavItem
                  path={item.path}
                  label={item.label}
                  icon={item.icon}
                  isMobile
                />
              </li>
            ))}
          </ul>
        </nav>

        {canViewAsCustomer && onToggleViewAsCustomer && (
          <div className="mobile-drawer__footer">
            <button
              className={`mobile-drawer__view-toggle ${viewAsCustomer ? 'mobile-drawer__view-toggle--active' : ''}`}
              onClick={() => { onToggleViewAsCustomer(); onClose(); }}
            >
              <EyeIcon style={{ width: 16, height: 16 }} />
              {viewAsCustomer ? 'Exit Customer View' : 'View as Customer'}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default MobileDrawer;
